/**
 * Blog Posts Management Page
 * Full CRUD implementation for blog posts with category support
 */

import { isBlankHtml } from "utils/htmlContent";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import OutlinedInput from "@mui/material/OutlinedInput";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import ImageUpload from "components/ImageUpload";
import RichTextEditor from "components/RichTextEditor";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { adminApi } from "services/api";

// Below md the DataTable shows each row as a card, which would print long text in full; clamp it
// there (column `clampLines`, default 3) so cards stay scannable. Desktop cells are unchanged.
function ClampedCell({ value, column }) {
  return (
    <MDBox
      component="span"
      color="inherit"
      sx={({ breakpoints }) => ({
        [breakpoints.down("md")]: {
          display: "-webkit-box",
          WebkitLineClamp: column.clampLines || 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        },
      })}
    >
      {value}
    </MDBox>
  );
}

ClampedCell.propTypes = {
  value: PropTypes.node,
  column: PropTypes.shape({ clampLines: PropTypes.number }).isRequired,
};

// Bigger hit area for the row's "more" icon on touch screens (below md only).
const phoneTapTarget = ({ breakpoints }) => ({
  [breakpoints.down("md")]: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 40,
    minHeight: 40,
  },
});

// Full-screen dialog (below sm): let the form content scroll between a pinned title and action bar.
const fullScreenForm = { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 };

function BlogPosts() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featuredImage: "",
    featured: false,
    published: true,
    categories: [],
    tags: [],
    tagsInput: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [menu, setMenu] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      // The server pages posts (10 by default) but this table pages them itself, so ask for all
      // of them — otherwise anything past the newest 10, drafts included, never appears.
      const response = await adminApi.getBlogPosts({ limit: 1000 });
      setPosts(response.posts || response);
      setLoading(false);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to fetch posts");
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApi.getBlogCategories();
      setCategories(response);
    } catch (err) {
      console.error("Fetch categories error:", err);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      featuredImage: "",
      featured: false,
      published: true,
      categories: [],
      tags: [],
      tagsInput: "",
    });
    setError("");
  };

  const handleEdit = (post) => {
    setOpen(true);
    setEditMode(true);
    setCurrentId(post._id);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || "",
      content: post.content,
      featuredImage: post.featuredImage || "",
      featured: post.featured || false,
      published: post.published,
      categories: post.categories?.map((cat) => cat._id || cat) || [],
      tags: post.tags || [],
      tagsInput: "",
    });
    setError("");
    handleCloseMenu();
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      featuredImage: "",
      featured: false,
      published: true,
      categories: [],
      tags: [],
      tagsInput: "",
    });
    setEditMode(false);
    setCurrentId(null);
    setError("");
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    const newValue = e.target.type === "checkbox" ? checked : value;
    setFormData({ ...formData, [name]: newValue });

    // Auto-generate slug from title
    if (name === "title" && !editMode) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData({ ...formData, title: value, slug });
    }
  };

  const handleCategoryChange = (event) => {
    const {
      target: { value },
    } = event;
    setFormData({
      ...formData,
      categories: typeof value === "string" ? value.split(",") : value,
    });
  };

  const handleTagsChange = (event) => {
    const value = event.target.value;
    // Keep the raw input value, don't split yet
    setFormData({ ...formData, tagsInput: value });
  };

  const handleTagsBlur = () => {
    // When user leaves the input, add any remaining tag
    const value = formData.tagsInput?.trim() || "";
    if (value) {
      const newTag = value;
      const newTags = [...formData.tags, newTag].filter(
        (tag, index, self) => self.indexOf(tag) === index // Remove duplicates
      );
      setFormData({ ...formData, tags: newTags, tagsInput: "" });
    }
  };

  const handleTagsKeyDown = (event) => {
    // When user presses Enter or comma, add the tag
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      const value = formData.tagsInput?.trim() || "";
      if (value) {
        const newTag = value.replace(",", "").trim(); // Remove comma if user typed it
        if (newTag && !formData.tags.includes(newTag)) {
          setFormData({ ...formData, tags: [...formData.tags, newTag], tagsInput: "" });
        } else {
          setFormData({ ...formData, tagsInput: "" });
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.slug || isBlankHtml(formData.content)) {
      setError("Title, slug, and content are required");
      return;
    }

    try {
      const submitData = {
        ...formData,
        categoryIds: formData.categories,
      };
      delete submitData.categories;

      if (editMode) {
        await adminApi.updateBlogPost(currentId, submitData);
        setSuccess("Post updated successfully");
      } else {
        await adminApi.createBlogPost(submitData);
        setSuccess("Post created successfully");
      }
      handleClose();
      fetchPosts();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      try {
        await adminApi.deleteBlogPost(id);
        setSuccess("Post deleted successfully");
        fetchPosts();
        setTimeout(() => setSuccess(""), 3000);
      } catch (err) {
        setError(err.response?.data?.error || "Delete failed");
      }
    }
    handleCloseMenu();
  };

  const openMenu = (event, post) => {
    setMenu(event.currentTarget);
    setSelectedPost(post);
  };

  const handleCloseMenu = () => {
    setMenu(null);
    setSelectedPost(null);
  };

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={Boolean(menu)}
      onClose={handleCloseMenu}
    >
      <MenuItem onClick={() => handleEdit(selectedPost)}>Edit</MenuItem>
      <MenuItem onClick={() => handleDelete(selectedPost._id)}>Delete</MenuItem>
    </Menu>
  );

  const columns = [
    { Header: "title", accessor: "title", width: "30%", Cell: ClampedCell },
    { Header: "slug", accessor: "slug", width: "20%", Cell: ClampedCell, clampLines: 2 },
    {
      Header: "categories",
      accessor: "categories",
      width: "20%",
      Cell: ClampedCell,
      clampLines: 2,
    },
    { Header: "status", accessor: "status", width: "15%" },
    { Header: "actions", accessor: "actions", width: "15%", align: "center" },
  ];

  const rows = posts.map((post) => ({
    title: post.title,
    slug: post.slug,
    categories: post.categories?.map((cat) => cat.name || "N/A").join(", ") || "-",
    status: (
      <MDTypography variant="caption" color={post.published ? "success" : "secondary"}>
        {post.published ? "Published" : "Draft"}
      </MDTypography>
    ),
    actions: (
      <MDTypography
        component="a"
        href="#"
        variant="caption"
        color="secondary"
        fontWeight="medium"
        aria-label="Post actions"
        sx={phoneTapTarget}
        onClick={(e) => {
          e.preventDefault();
          openMenu(e, post);
        }}
      >
        <Icon>more_vert</Icon>
      </MDTypography>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={3}>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Card>
            <MDBox
              p={{ xs: 2, md: 3 }}
              display="flex"
              flexWrap="wrap"
              gap={1.5}
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">Blog Posts</MDTypography>
              <MDButton variant="gradient" color="info" onClick={handleOpen}>
                <Icon>add</Icon>&nbsp; Add Post
              </MDButton>
            </MDBox>
            <DataTable table={{ columns, rows }} canSearch />
          </Card>
        </MDBox>
      </MDBox>

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>{editMode ? "Edit Post" : "Add New Post"}</DialogTitle>
        <form onSubmit={handleSubmit} style={fullScreenDialog ? fullScreenForm : undefined}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <MDBox mb={2}>
              <MDInput
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </MDBox>
            <MDBox mb={2}>
              <RichTextEditor
                label="Content *"
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                error={!formData.content && error}
                helperText="Use the toolbar to format text, insert images, embed videos, and more"
                height={400}
              />
            </MDBox>
            <MDBox mb={2}>
              <ImageUpload
                currentImage={formData.featuredImage}
                onImageChange={(url) => setFormData({ ...formData, featuredImage: url })}
                label="Featured Image"
              />
            </MDBox>
            <MDBox mb={2}>
              <FormControl fullWidth>
                <InputLabel id="categories-label">Categories</InputLabel>
                <Select
                  labelId="categories-label"
                  multiple
                  value={formData.categories}
                  onChange={handleCategoryChange}
                  input={<OutlinedInput label="Categories" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const cat = categories.find((c) => c._id === value);
                        return <Chip key={value} label={cat?.name || value} />;
                      })}
                    </Box>
                  )}
                >
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Tags"
                name="tags"
                value={formData.tagsInput}
                onChange={handleTagsChange}
                onBlur={handleTagsBlur}
                onKeyDown={handleTagsKeyDown}
                fullWidth
                placeholder="Type a tag and press Enter or comma"
                helperText="Press Enter or comma after each tag"
              />
              {formData.tags.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={() => {
                        const newTags = formData.tags.filter((_, i) => i !== index);
                        setFormData({ ...formData, tags: newTags });
                      }}
                    />
                  ))}
                </Box>
              )}
            </MDBox>
            <MDBox mb={2}>
              <FormControlLabel
                control={
                  <Switch checked={formData.featured} onChange={handleChange} name="featured" />
                }
                label="Featured Post"
              />
            </MDBox>
            <MDBox mb={2}>
              <FormControlLabel
                control={
                  <Switch checked={formData.published} onChange={handleChange} name="published" />
                }
                label="Published"
              />
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleClose} color="secondary">
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info">
              {editMode ? "Update" : "Create"}
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>

      {renderMenu}
    </DashboardLayout>
  );
}

export default BlogPosts;
